import React from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Play,
  Clock,
  MoreVertical,
  Trash2,
  Share2,
  RefreshCw,
  X,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import PlayBtn from '../ui/PlayBtn';
import {
  formatDuration,
  getGeneratingProgress,
  isNewTagVideoCard,
} from '../../utils/VideoUtils';
import { formatTimeAgo } from '../../utils/DateUtils';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import type { VideoCard } from '../../model/home';
import ImageVideoGenerating from '../../assets/video-generating.png';
import ImageVideoFailed from '../../assets/video-generate-fail.png';
import ImageNewTag from '../../assets/new.png';
import ImageRetry from '../../assets/refresh.png';

export interface SubjectColor {
  bg: string;
  text: string;
}

export interface MyVideoCardProps {
  video: VideoCard;
  index: number;
  subjectColorMap: Record<string, SubjectColor>;
  onVideoClick: (videoId: string) => void;
  onShare: (videoId: string) => void;
  onDelete: (videoId: string) => void;
  onCancelGenerate?: (videoId: string) => void;
  onReGenerate?: (videoId: string) => void;
}

export function MyVideoCard({
  video,
  index,
  subjectColorMap,
  onVideoClick,
  onShare,
  onDelete,
  onCancelGenerate,
  onReGenerate,
}: MyVideoCardProps) {
  const isGenerating = video.status === 'v_proc' || video.status === 'q_proc';
  const isComplete = video.status === 'v_succ';
  const isNewCard = isNewTagVideoCard(video);
  const isFailed = video.status === 'v_fail' || video.status === 'q_fail';
  const videoSubject = isComplete ? video.subject || 'Others' : 'Others';
  const subjectColor = subjectColorMap[videoSubject]
    ? subjectColorMap[videoSubject]
    : subjectColorMap['Others'];

  return (
    <Card
      onClick={() => !isGenerating && !isFailed && onVideoClick(video.video_id)}
      className={`relative min-w-[275px] w-[275px] shrink-0 transition-all duration-300 gap-2 border-none ${
        !isGenerating && !isFailed
          ? 'cursor-pointer hover:bg-primary'
          : 'cursor-default'
      } ${isNewCard ? 'video-card-slide-in' : 'animate-in fade-in slide-in-from-right-12'}`}
      style={{ animationDelay: `${index * 100}ms` }}
    >
      {/* 新卡片增加New的样式 */}
      {isNewCard && (
        <div
          className="absolute top-3 right-3 z-10 text-white text-base font-medium"
          style={{
            textShadow:
              '-1px -1px 0 #991b1b, 0 -1px 0 #991b1b, 1px -1px 0 #991b1b, 1px 0 0 #991b1b, 1px 1px 0 #991b1b, 0 1px 0 #991b1b, -1px 1px 0 #991b1b, -1px 0 0 #991b1b',
          }}
        >
          <img src={ImageNewTag} className="block w-8" />
        </div>
      )}

      {/* Thumbnail */}
      <div
        className={`relative aspect-video flex items-center justify-center overflow-hidden border ${
          isGenerating ? 'glass-loading-container' : ''
        }`}
        style={{
          background: isGenerating
            ? 'transparent'
            : isFailed
              ? `url('https://cdn.tiantiantiaosheng.com/dataupload/tool/2025-11-05/a58ee59c-0051-42c4-8ada-50b7d2f06838/f671992e-fe91-4e5c-aa5e-3cfb365b2853.png') center/cover`
              : `url(${video.cover_img || 'https://cdn.jumpstar.app/solve/ai_gen/20251201/9f2c75b4-6e7d-4380-9ca5-c88d066b5c1f.png'}) center center / 100% no-repeat`,
          borderRadius: '12px',
        }}
      >
        {/* 正在生成中展示的内容 或 生成失败展示内容 */}
        {(isGenerating || isFailed) && (
          // <>
          //   <div className="glass-blur"></div>
          //   <div className="glass-gradient"></div>
          //   <div className="glass-shimmer"></div>
          // </>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              height: '100%',
              borderRadius: '12px',
              border: '1px solid rgba(0, 0, 0, 0.10);',
              background:
                'linear-gradient(0deg, rgba(0, 0, 0, 0.50) 0%, rgba(0, 0, 0, 0.50) 100%), linear-gradient(135deg, rgba(10, 0, 104, 0.74) 0%, rgba(118, 166, 146, 0.33) 59.75%, rgba(255, 80, 156, 0.33) 100%)',
            }}
          >
            {/* 生成中内容展示 */}
            {isGenerating && (
              <>
                <div>
                  <img src={ImageVideoGenerating} className="block w-11" />
                </div>
                <div className="text-white text-sm font-medium">
                  <span>Generating</span>
                  <span className="ml-1">{getGeneratingProgress(video)}%</span>
                </div>
              </>
            )}

            {/* 生成失败内容展示 */}
            {isFailed && (
              <>
                <div>
                  <img src={ImageVideoFailed} className="block w-11" />
                </div>
                <div className="text-white text-sm font-medium opacity-50">
                  <span>Something went wrong</span>
                </div>
                <div
                  className="text-white text-sm font-medium bg-black/30 rounded-full px-3 py-0.5 border border-black/5 mt-1"
                  onClick={() => onReGenerate?.(video.video_id)}
                >
                  <img src={ImageRetry} alt="" className="w-4 inline-block" />
                  <span className="inline-block ml-1">Retry</span>
                </div>
              </>
            )}
          </div>
        )}

        {/* 生成成功展示内容 */}
        {!isGenerating && !isFailed && (
          <>
            <PlayBtn />
          </>
        )}

        {!isGenerating && !isFailed && (
          <div className="absolute bottom-2 right-2">
            <div className="bg-black/30 text-white text-xs px-2 py-0.5 rounded">
              {formatDuration(Math.floor(video.video_duration ?? 0))}
            </div>
          </div>
        )}

        {isComplete && (
          <div className="absolute top-2 left-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 bg-white/95 hover:bg-white shadow-[0px_4px_6px_-1px_rgba(0,0,0,0.1),0px_2px_4px_-2px_rgba(0,0,0,0.1)] rounded-full"
                  onClick={(e) => e.stopPropagation()}
                >
                  <MoreVertical className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-40 rounded-2xl">
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    onShare(video.video_id);
                  }}
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Share
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(video.video_id);
                  }}
                  className="text-red-600 focus:text-red-600"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>

      {/* 后半部分内容 Content */}
      <div className="py-3 pt-0.5 flex flex-col h-[86px]">
        <div className="flex items-center justify-between mb-1">
          {isGenerating ? (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              {/* <Clock className="w-3.5 h-3.5" />
              <span>
                {(() => {
                  const elapsed = Math.floor(
                    (Date.now() - (video.create_ts || 0) * 1000) /
                      1000,
                  );
                  const totalSeconds = 10 * 60;
                  const progressPercent = Math.min(
                    Math.floor((elapsed / totalSeconds) * 90),
                    90,
                  );
                  return `Generating ${progressPercent}%`;
                })()}
              </span> */}
            </div>
          ) : (
            <Badge
              className={`${subjectColor.bg} ${subjectColor.text} rounded-full px-3 py-0.5 border border-black/5`}
            >
              {videoSubject}
            </Badge>
          )}
          <span className="text-xs text-muted-foreground">
            {formatTimeAgo(new Date(video.create_ts * 1000))}
          </span>
        </div>
        <h4 className="line-clamp-2 min-h-12 mb-2">
          <ReactMarkdown
            remarkPlugins={[remarkMath]}
            rehypePlugins={[rehypeKatex]}
          >
            {video.title}
          </ReactMarkdown>
        </h4>

        {/* 底部的状态条，目前不需要 */}
        {/* <div className="grow"></div>

        {isGenerating && (
          <div className="flex items-center gap-2">
            <Button className="flex-1 rounded-2xl flex items-center justify-start gap-2 bg-[#302f2b] hover:bg-[#302f2b]/90 text-[#ffe75c] h-9">
              <div className="bar-wave-home">
                <span></span>
                <span></span>
                <span></span>
              </div>
              <span>Generating...</span>
            </Button>
            <Button
              onClick={(e) => {
                e.stopPropagation();
                onCancelGenerate?.(video.video_id);
              }}
              className="rounded-2xl flex items-center justify-center bg-gray-200 hover:bg-gray-300 text-gray-700 h-9 px-3"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        )}

        {isFailed && (
          <div className="flex items-center gap-2">
            <Button
              disabled
              className="flex-1 rounded-2xl flex items-center justify-start gap-2 bg-[#f0f0f0] hover:bg-[#f0f0f0] text-black h-9 cursor-not-allowed"
            >
              <span>Generation failed</span>
            </Button>
            <Button
              onClick={(e) => {
                e.stopPropagation();
                onReGenerate?.(video.video_id);
              }}
              className="rounded-2xl flex items-center justify-center bg-[#302f2b] hover:bg-[#302f2b]/90 text-[#ffe75c] h-9 px-3"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        )}

        {isComplete && (
          <Button
            onClick={(e) => {
              e.stopPropagation();
              onVideoClick(video.video_id);
            }}
            className="w-full rounded-2xl flex items-center justify-start gap-2 bg-[#302f2b] hover:bg-[#302f2b]/90 text-[#ffe75c] h-9"
          >
            <Play className="w-4 h-4" />
            <span>Ready to play</span>
          </Button>
        )} */}
      </div>
    </Card>
  );
}
